import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import useQuestionnaireStore from "@/store/useQuestionnaireStore";
import analytics from "@/utils/analytics";
import { getReferralCode } from "@/utils/referralTracking";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Questionnaire = () => {
  const navigate = useNavigate();
  const {
    visibleQuestions,
    currentStep,
    answers,
    isLoading,
    isSubmitting,
    setQuestions,
    setLoading,
    setSubmitting,
    setAnswer,
    nextStep,
    prevStep,
    isCurrentQuestionAnswered,
    getProgress,
    setResult,
    reset,
    isLastQuestion
  } = useQuestionnaireStore();

  const hasTrackedStart = useRef(false);

  // Fetch questions on mount
  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/questions`);
        setQuestions(response.data);
        
        // Track check started (only once)
        if (!hasTrackedStart.current) {
          analytics.dbaCheckStarted();
          hasTrackedStart.current = true;
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
        toast.error("Er ging iets mis bij het laden van de vragen");
      } finally {
        setLoading(false);
      }
    };

    reset();
    fetchQuestions();
  }, [setQuestions, setLoading, reset]);

  const handleOptionSelect = (questionId, optionId, score) => {
    setAnswer(questionId, optionId, score);
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      // Get referral code if present
      const referralCode = getReferralCode();
      
      // Submit answers without personal data - show result immediately
      const submissionData = {
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          question_id: questionId,
          option_id: answer.optionId,
          score: answer.score
        })),
        // Include referral code if present
        ...(referralCode && { referral_code: referralCode })
      };

      const response = await axios.post(`${API_URL}/submissions`, submissionData);
      setResult(response.data);
      
      // Track completion
      analytics.dbaCheckCompleted(
        response.data.id, 
        response.data.normalized_score, 
        response.data.outcome
      );
      
      navigate(`/dba-check/resultaat/${response.data.id}`);
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Er ging iets mis bij het verwerken. Probeer het opnieuw.");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" data-testid="loading-spinner" />
      </div>
    );
  }

  const currentQuestion = visibleQuestions[currentStep];
  const progress = getProgress();
  const lastQuestion = isLastQuestion();

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header with progress */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate("/dba-check")}
              className="text-zinc-600 hover:text-zinc-900 transition-colors flex items-center gap-2 text-sm"
              data-testid="back-to-landing-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Terug naar overzicht
            </button>
            <span className="text-sm text-zinc-500" data-testid="progress-text">
              Vraag {currentStep + 1} van {visibleQuestions.length}
            </span>
          </div>
          <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
              data-testid="progress-bar"
            />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="px-6 py-12 md:py-16">
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white border border-zinc-200/80 shadow-md shadow-zinc-200/20 rounded-2xl p-8 md:p-12"
              data-testid="question-card"
            >
              <p className="text-sm uppercase tracking-[0.15em] font-bold text-blue-600 mb-4">
                {currentQuestion?.category && currentQuestion.category.charAt(0).toUpperCase() + currentQuestion.category.slice(1)}
              </p>
              <h2 
                className="font-outfit text-xl sm:text-2xl font-medium text-zinc-900 mb-2"
                data-testid="question-text"
              >
                {currentQuestion?.question_text}
              </h2>
              
              {/* Description if available */}
              {currentQuestion?.description && (
                <p className="text-sm text-zinc-500 mb-4">
                  {currentQuestion.description}
                </p>
              )}
              
              {/* Info text if available */}
              {currentQuestion?.info_text && (
                <p className="text-sm text-zinc-500 mb-6 bg-zinc-50 rounded-lg px-4 py-3 border border-zinc-100">
                  💡 {currentQuestion.info_text}
                </p>
              )}

              <div className="space-y-3">
                {currentQuestion?.options.map((option, index) => {
                  const isSelected = answers[currentQuestion.id]?.optionId === option.id;
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleOptionSelect(currentQuestion.id, option.id, option.score)}
                      className={`w-full text-left border-2 rounded-xl p-4 md:p-5 transition-all cursor-pointer ${
                        isSelected
                          ? "border-blue-600 bg-blue-50/80 shadow-sm"
                          : "border-zinc-200 hover:border-blue-400/50 hover:bg-blue-50/50"
                      }`}
                      data-testid={`option-${index}`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                            isSelected
                              ? "border-blue-600 bg-blue-600"
                              : "border-zinc-300"
                          }`}
                        >
                          {isSelected && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <span className={`text-base ${isSelected ? "text-zinc-900" : "text-zinc-700"}`}>
                          {option.text}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="rounded-full px-6 py-5 border-zinc-300 text-zinc-700 hover:bg-zinc-100"
              data-testid="prev-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Vorige
            </Button>

            {!lastQuestion ? (
              <Button
                onClick={nextStep}
                disabled={!isCurrentQuestionAnswered()}
                className="rounded-full px-8 py-5 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white btn-hover disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-btn"
              >
                Volgende
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!isCurrentQuestionAnswered() || isSubmitting}
                className="rounded-full px-8 py-5 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white btn-hover disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verwerken...
                  </>
                ) : (
                  <>
                    Bekijk resultaat
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Questionnaire;
